/**
 * Read encoded customer email from storage for the buy-again widget.
 * Email is stored by the thank-you page (Shopify extension) encoded with a secret.
 * Decoding happens server-side only; the widget never has the secret.
 * Extension storage is in localStorage under "extensionStorage" with keys like
 * "v1-gid://shopify/App/...-customer-email" and the encoded value.
 * When we read from extension storage we also persist to a fallback key so the
 * widget still works if extension storage is unavailable later.
 */

const EXTENSION_STORAGE_KEY = "extensionStorage";
const CUSTOMER_EMAIL_SUFFIX = "customer-email";
const FALLBACK_STORAGE_KEY = "buy-again-encoded-email";

/** Get the raw (encoded) value from extensionStorage. Used to send to API; server decodes. */
export function getStoredRawValue(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const fromExtension = readFromExtensionStorage();
    if (fromExtension) {
      window.localStorage.setItem(FALLBACK_STORAGE_KEY, fromExtension);
      return fromExtension;
    }
    const fromFallback = window.localStorage.getItem(FALLBACK_STORAGE_KEY);
    return typeof fromFallback === "string" && fromFallback.length > 0
      ? fromFallback
      : null;
  } catch {
    return null;
  }
}

function readFromExtensionStorage(): string | null {
  const raw = window.localStorage.getItem(EXTENSION_STORAGE_KEY);
  if (!raw) return null;
  const data = JSON.parse(raw) as Record<string, string>;
  const key = Object.keys(data).find((k) => k.includes(CUSTOMER_EMAIL_SUFFIX));
  if (!key) return null;
  const value = data[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}
